import {
  ISimpleEventDetail,
  ISubTicketItem,
  Phase,
  SupportedLanguage,
} from "interfaces";
import { PRICE_TYPES } from "utills/constants";
import { get } from "lodash";
import { Label } from "components/label";
import { SwitchButton } from "components/switchButton";
import { RightSideModal } from "components/rightSideModal";
import { IModalProps } from "components/modal";
import moment from "moment";
import { currencySymbol } from "utills/helpers";
import { useTranslation } from "@refinedev/core";

interface SubTicketProps extends IModalProps {
  eventDetailData: ISimpleEventDetail;
  ticketPhases: Phase[];
  subTicketData?: ISubTicketItem | null;
}

export const SubTicket = ({
  title,
  open,
  onClose,
  eventDetailData,
  ticketPhases,
  subTicketData,
}: SubTicketProps) => {
  const { translate, getLocale } = useTranslation();
  const firstLanguage = eventDetailData.languages.includes(
    getLocale() as SupportedLanguage
  )
    ? getLocale() || "en"
    : eventDetailData.languages[0] ?? "vi";

  const ticketPhaseArray = ticketPhases.map((phase) => ({
    label: get(phase, `name.${firstLanguage}`, ""),
    value: get(phase, "id", ""),
  }));

  return (
    <RightSideModal title={title ?? ""} open={open} onClose={onClose}>
      <div className="grid sm:grid-cols-6 gap-y-6 gap-x-4 p-4 text-sm sm:px-6">
        <div className="col-span-full">
          <div className="flex flex-row flex-wrap gap-x-3">
            {PRICE_TYPES.map((type) => (
              <div
                key={type.value}
                className={`rounded-md ${
                  type.value === subTicketData?.price_type
                    ? "bg-[#A7B4FE] hover:bg-indigo-300 focus-visible:outline-indigo-300"
                    : "bg-white ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                } px-6 py-3 text-sm font-semibold text-brand-500 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
              >
                {type.label}
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
          <p className="text-sm">
            {translate("eventSetup.phaseAndTicket.modal.ticket.subTicketInfo")}
          </p>
          {subTicketData?.id ? (
            <p className="text-sm mt-1">
              {translate("eventSetup.phaseAndTicket.modal.ticket.productId")}:{" "}
              {subTicketData?.id}
            </p>
          ) : (
            <></>
          )}
        </div>
        {/* <div className="col-span-full flex items-center justify-between font-medium">
          <p className="text-sm">Category</p>
          <p>{get(subTicketData, `category.${firstLanguage}`) || <span className="text-gray-400">N/A</span>}</p>
        </div> */}

        <div className="col-span-full">
          <Label
            id="ticket_phase"
            name={translate(
              "eventSetup.phaseAndTicket.modal.ticket.ticketPhase"
            )}
            required
          />
          {
            ticketPhaseArray.find(
              (selected: { label: string; value: string }) =>
                selected.value === subTicketData?.ticket_phase
            )?.label
          }
        </div>

        <div className="col-span-full">
          <Label
            required
            id={`type_name_${eventDetailData.languages[0] ?? "en"}`}
            name={translate(
              "eventSetup.phaseAndTicket.modal.ticket.ticketNameDisplayed"
            )}
          />
          <div className="flex flex-col gap-y-3">
            {eventDetailData.languages.map((lang: SupportedLanguage) => (
              <div key={lang}>
                <div className="relative flex items-center gap-x-2">
                  <img
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className="size-5"
                  />
                  {get(subTicketData?.type_name, lang, "")}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-full">
          <Label
            id={`short_description_${eventDetailData.languages[0] ?? "en"}`}
            name={translate(
              "eventSetup.phaseAndTicket.modal.ticket.noteBelowTicketName"
            )}
          />
          <div className="flex flex-col gap-y-3">
            {eventDetailData.languages.map((lang: SupportedLanguage) => (
              <div key={lang}>
                <div className="relative flex items-center gap-x-2">
                  <img
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className="size-5"
                  />
                  {get(subTicketData?.short_description, lang, "") || (
                    <span className="text-gray-400">N/A</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-full">
          <Label
            id="total_for_sell"
            name={translate(
              "eventSetup.phaseAndTicket.modal.ticket.totalForSell"
            )}
          />
          {get(subTicketData, "total_for_sell", null) || (
            <span className="text-gray-400">N/A</span>
          )}
        </div>
        <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
          <p className="text-sm">
            {translate("eventSetup.phaseAndTicket.modal.ticket.priceInfo")}
          </p>
        </div>
        <div className="sm:col-span-3">
          <Label
            id="original_price"
            name={translate(
              "eventSetup.phaseAndTicket.modal.ticket.originalPrice"
            )}
            required
          />
          {get(subTicketData, "original_price", 0)}{" "}
          {currencySymbol(eventDetailData.currency)}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="price"
            name={translate("eventSetup.phaseAndTicket.modal.ticket.sellPrice")}
            required
          />
          {get(subTicketData, "price", 0)}{" "}
          {currencySymbol(eventDetailData.currency)}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="sell_from"
            name={translate("eventSetup.phaseAndTicket.modal.ticket.startDate")}
            required
          />
          {subTicketData?.sell_from
            ? moment(subTicketData.sell_from).format("HH:mm DD/MM/YYYY")
            : ""}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="sell_to"
            name={translate("eventSetup.phaseAndTicket.modal.ticket.endDate")}
            required
          />
          {subTicketData?.sell_to
            ? moment(subTicketData.sell_to).format("HH:mm DD/MM/YYYY")
            : ""}
        </div>

        <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
          <p className="text-sm">
            {translate(
              "eventSetup.phaseAndTicket.modal.ticket.advanceSettings"
            )}
          </p>
        </div>
        <div className="col-span-full flex flex-col items-start gap-y-3">
          <SwitchButton
            label={translate("eventSetup.phaseAndTicket.modal.ticket.show")}
            hint={translate("eventSetup.phaseAndTicket.modal.ticket.showHint")}
            checked={!subTicketData?.hide_in_ui}
            onChange={() => {}}
            disabled
          />
          <SwitchButton
            label={translate(
              "eventSetup.phaseAndTicket.modal.ticket.acceptEditParticipantInfo"
            )}
            hint={translate(
              "eventSetup.phaseAndTicket.modal.ticket.acceptEditParticipantInfoHint"
            )}
            checked={subTicketData?.is_accept_edit_participant_info ?? false}
            onChange={() => {}}
            disabled
          />
          <SwitchButton
            label={translate(
              "eventSetup.phaseAndTicket.modal.ticket.acceptUpgradeTicket"
            )}
            hint={translate(
              "eventSetup.phaseAndTicket.modal.ticket.acceptUpgradeTicketHint"
            )}
            checked={subTicketData?.is_accept_upgrade_ticket ?? false}
            onChange={() => {}}
            disabled
          />
          <SwitchButton
            label={translate(
              "eventSetup.phaseAndTicket.modal.ticket.acceptTransferTicket"
            )}
            hint={translate(
              "eventSetup.phaseAndTicket.modal.ticket.acceptTransferTicketHint"
            )}
            checked={subTicketData?.is_accept_transfer_ticket ?? false}
            onChange={() => {}}
            disabled
          />
          <SwitchButton
            label={translate(
              "eventSetup.phaseAndTicket.modal.ticket.isSoldOut"
            )}
            hint={translate(
              "eventSetup.phaseAndTicket.modal.ticket.isSoldOutHint"
            )}
            checked={subTicketData?.is_soldout ?? false}
            onChange={() => {}}
            disabled
          />
        </div>
      </div>
    </RightSideModal>
  );
};
